import * as Sentry from "@sentry/react";

// Errors caused by browser extensions (Google Translate, Grammarly, etc.)
// that modify the DOM and break React reconciliation.
const BROWSER_EXTENSION_ERRORS = [
  "Failed to execute 'removeChild' on 'Node'",
  "Failed to execute 'insertBefore' on 'Node'",
  "Failed to execute 'appendChild' on 'Node'",
  "The node to be removed is not a child of this node",
  "Node.removeChild",
  "Node.insertBefore",
];

// Errors from third-party in-app browser WebViews (Instagram, Facebook, TikTok, etc.)
// that inject their own JS which breaks outside their native bridge context.
const WEBVIEW_BRIDGE_ERRORS = [
  "Java object is gone",
  "window.webkit.messageHandlers",
  "enableDidUserTypeOnKeyboardLogging",
  "IABTranslationJS",
  "hxp-chat-suppression",
  "setContactAutofillValuesFromBridge",
  // Instagram Android: o `iabjs://navigation_performance_logger` tenta mandar
  // telemetria pro app nativo no beforeunload disparado pelo nosso CTA, e o
  // bridge Java falha. O erro é causado pelo CTA FUNCIONANDO — a pessoa está
  // saindo da página rumo ao checkout. 337 ocorrências desde 07/2026.
  "Error invoking postMessage",
  // Instagram iOS: rejeição de promise do próprio in-app browser, sem
  // stacktrace nosso. Visto em /p/ com lead entrando 29s depois.
  "Handling is disabled",
];

// A página pública do quiz (/p/:id) é alto volume e roda em tablet/celular.
// O Session Replay (rrweb) faz um snapshot do DOM inteiro no load + grava cada
// interação — pesa no TBT/LCP e ainda capturaria PII dos leads no formulário.
// Aqui ele NÃO carrega na rota pública; o monitoramento de ERROS (exceptions,
// stack trace, breadcrumbs) continua ativo em todas as páginas.
const isPublicPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/p/');

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(isPublicPage ? [] : [Sentry.replayIntegration()]),
  ],
  // Performance Monitoring - 10% é suficiente p/ tendência sem pesar no load.
  tracesSampleRate: 0.1,
  // Session Replay - 10% normal, 100% on error (não roda na página pública)
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Só em build de produção E com DSN — dev local (vite) reportava erros de
  // teste/desenvolvimento como environment:production e disparava alerta falso.
  enabled: import.meta.env.PROD && !!import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  beforeSend(event) {
    const message = event.exception?.values?.[0]?.value || '';
    if (BROWSER_EXTENSION_ERRORS.some((err) => message.includes(err))) {
      return null;
    }
    // In-app browser WebView bridge errors (Instagram, Facebook, TikTok, etc.)
    const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    const stackStr = frames.map((f) => f.function || '').join(' ');
    if (WEBVIEW_BRIDGE_ERRORS.some((err) => message.includes(err) || stackStr.includes(err))) {
      return null;
    }
    // Stale chunk after deploy — user just needs to reload
    if (message.includes('Failed to fetch dynamically imported module') || message.includes('Importing a module script failed')) {
      return null;
    }
    return event;
  },
});
